import { useRef, useState, useMemo, useEffect, useCallback } from 'react'
import { arrayMove } from '@dnd-kit/sortable'
import { useParams, useBlocker } from 'react-router-dom'
import { Loader2, AlertCircle, AlertTriangle, RotateCcw, Settings2, Eye, X, Save, Paperclip, ChevronDown, ExternalLink, History, Check, CalendarCheck, XCircle, PenLine, Lock, ArrowRight } from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { useReviewArticle, useSaveReviewArticle, articleKeys, useWorkflowAction, useArticleNotes, useCreateNote, useDeleteNote } from '../../api/hooks'
import { useServices } from '../../api/ServiceContext'
import { useWireAuth } from '../../auth/useWireAuth'
import { ArticleTocShelf } from './ArticleTocShelf'
import { ArticleCanvas } from './ArticleCanvas'
import { ArticleBlockInspector } from './ArticleBlockInspector'
import { ArticleMetadataDrawer } from './ArticleMetadataDrawer'
import { ArticleArtifactsDrawer } from './ArticleArtifactsDrawer'
import { ArticleChangelogDrawer } from './ArticleChangelogDrawer'
import { ArticleRevisionsDrawer } from './ArticleRevisionsDrawer'
import type { WireArtifact, WireArticleBlock, WireArticleFull, WireWorkflowStage, WireImageArtifact, WireVisualArtifact, WireVideoArtifact, WireCaptionsArtifact, WireAudioArtifact, WireInlineBlock } from '../../api/types'
import { validateArticle } from './validator'
import { applyResolution } from './validator/resolutions'
import type { ValidationError } from './validator/types'
import { ArticleValidationBanner } from './ArticleValidationBanner'
import { ArticleRenderer } from '../article-viewer/ArticleRenderer'
import { DarkModeToggle } from '../DarkModeToggle'
import Navbar from '../navbar'
import { useArticlePreviewBroadcast } from '../../hooks/useArticlePreviewBroadcast'
import { getArticleToolbarWorkflowActions, getWorkflowActionAccess } from '../../workflow/actionAccess'
import { useAuthz } from '../../auth/useAuthz'

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_META: Record<WireWorkflowStage, { label: string; className: string }> = {
  qc_review:           { label: 'QC Review',  className: 'bg-yellow-3 text-yellow-11 border border-yellow-7' },
  layout_review:       { label: 'Layout',      className: 'bg-blue-3 text-blue-11 border border-blue-7' },
  editorial_approval:  { label: 'Editorial',   className: 'bg-violet-3 text-violet-11 border border-violet-7' },
  scheduled:           { label: 'Scheduled',   className: 'bg-cyan-3 text-cyan-11 border border-cyan-7' },
  published:           { label: 'Published',   className: 'bg-green-3 text-green-11 border border-green-7' },
  post_pub_review:     { label: 'Post-Pub',    className: 'bg-orange-3 text-orange-11 border border-orange-7' },
  recalled:            { label: 'Recalled',    className: 'bg-red-3 text-red-11 border border-red-7' },
  archived:            { label: 'Archived',    className: 'bg-fill text-fg-secondary border border-line opacity-50' },
}

function StatusBadge({ stage }: { stage: WireWorkflowStage }) {
  const { label, className } = STATUS_META[stage] ?? { label: stage, className: 'bg-fill text-fg-secondary border border-line' }
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${className}`}>
      {label}
    </span>
  )
}

// ─── ArticleReviewer ──────────────────────────────────────────────────────────

export function ArticleReviewer() {
  const { id = '' } = useParams<{ id: string }>()
  const { data: article, isPending, isError, error } = useReviewArticle(id)

  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null)
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null)
  const [tocOpen, setTocOpen] = useState(false)
  const [blockPaletteOpen, setBlockPaletteOpen] = useState(false)
  const [metaDrawerOpen, setMetaDrawerOpen] = useState(false)
  const [artifactsDrawerOpen, setArtifactsDrawerOpen] = useState(false)
  const [changelogDrawerOpen, setChangelogDrawerOpen] = useState(false)
  const [revisionsDrawerOpen, setRevisionsDrawerOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null)
  const [approveDialogOpen, setApproveDialogOpen] = useState(false)
  const [scheduleDate, setScheduleDate] = useState('')
  const [postPubRevisionDialogOpen, setPostPubRevisionDialogOpen] = useState(false)
  const [postPubRevisionNote, setPostPubRevisionNote] = useState('')
  // Local copy of the article with unsaved block/metadata edits applied
  const [localArticle, setLocalArticle] = useState<WireArticleFull | null>(null)
  // Artifact additions/deletions — tracked separately so they don't affect dirty state
  // (backend ignores the artifacts key on PUT /drafts/:id)
  const [localArtifacts, setLocalArtifacts] = useState<WireArtifact[] | null>(null)
  const blockRefs = useRef<Record<string, HTMLElement | null>>({})
  const queryClient = useQueryClient()
  const saveMutation = useSaveReviewArticle()
  const workflowAction = useWorkflowAction()
  const { data: allNotes } = useArticleNotes(id, true)
  const createNote = useCreateNote(id)
  const deleteNote = useDeleteNote(id)
  const { user } = useWireAuth()
  const { isWorkflowMember, isEditorialManager, isPostProduction } = useAuthz()
  const canIssueRevision = isEditorialManager || isPostProduction
  const currentUserSub = user?.username ?? user?.sub ?? ''
  const isDirty = localArticle !== null

  // Block in-app navigation (link clicks, navigate(), browser back/forward within SPA)
  const blocker = useBlocker(useCallback(() => isDirty, [isDirty]))

  // Block hard browser unload (refresh, close tab, type new URL)
  useEffect(() => {
    if (!isDirty) return
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  // After an artifact upload invalidates and re-fetches the article, sync fresh
  // presignedUrls from the server into localArtifacts so images render immediately.
  useEffect(() => {
    if (!article?.artifacts || localArtifacts === null) return
    const serverMap = new Map(article.artifacts.map(a => [a.id, a]))
    setLocalArtifacts(prev => {
      if (!prev) return prev
      const next = prev.map(a => serverMap.get(a.id) ?? a)
      return next.every((a, i) => a === prev[i]) ? prev : next
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article?.artifacts])

  function handleSectionClick(blockId: string) {
    setActiveSectionId(blockId)
    const el = blockRefs.current[blockId]
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  function handleSaveEdit(blockId: string, newChildren: WireInlineBlock[]) {
    setLocalArticle(current => {
      const base = current ?? article
      if (!base) return current
      return {
        ...base,
        blocks: base.blocks.map(b =>
          b.id === blockId && b.type === 'paragraph'
            ? { ...b, children: newChildren }
            : b
        ),
      }
    })
    setEditingBlockId(null)
  }

  function handleSaveBlockMeta(blockId: string, patch: { classification: string; disseminationControls?: string[] }) {
    setLocalArticle(current => {
      const base = current ?? article
      if (!base) return current
      return {
        ...base,
        blocks: base.blocks.map(b => b.id === blockId ? { ...b, ...patch } : b),
      }
    })
  }

  function handlePostPubRevision() {
    if (!safeArticle || !postPubRevisionNote.trim()) return
    workflowAction.mutate(
      { type: 'post_pub_revision', id: safeArticle.id, by: currentUserSub, byName: user?.name, note: postPubRevisionNote.trim() },
      {
        onSuccess: () => {
          setPostPubRevisionDialogOpen(false)
          setPostPubRevisionNote('')
          toast.success('Sent for revision — article remains live, QC notified')
        },
        onError: () => toast.error('Failed to request revision'),
      },
    )
  }

  function defaultScheduleDatetime() {
    const d = new Date()
    d.setMinutes(0, 0, 0)
    d.setHours(d.getHours() + 1)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:00`
  }

  function handleEditorialApprove(publishNow: boolean) {
    if (!safeArticle) return
    const scheduledFor = publishNow ? new Date().toISOString() : new Date(scheduleDate).toISOString()
    workflowAction.mutate(
      { type: 'editorial_approve', id: safeArticle.id, by: currentUserSub, byName: user?.name, scheduledFor },
      {
        onSuccess: () => {
          setApproveDialogOpen(false)
          setScheduleDate('')
          toast.success(publishNow ? 'Approved — publishing now' : 'Approved — scheduled for publication')
        },
        onError: () => toast.error('Failed to record approval'),
      },
    )
  }

  function handleUpdateBlock(blockId: string, updated: WireArticleBlock) {    setLocalArticle(current => {
      const base = current ?? article
      if (!base) return current
      return {
        ...base,
        blocks: base.blocks.map(b => b.id === blockId ? updated : b),
      }
    })
  }

  function handleMoveBlock(blockId: string, direction: 'up' | 'down') {
    setLocalArticle(current => {
      const base = current ?? article
      if (!base) return current
      const blocks = [...base.blocks]
      const idx = blocks.findIndex(b => b.id === blockId)
      if (idx < 0) return current
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1
      if (swapIdx < 0 || swapIdx >= blocks.length) return current
      ;[blocks[idx], blocks[swapIdx]] = [blocks[swapIdx], blocks[idx]]
      return { ...base, blocks }
    })
  }

  function handleDeleteBlock(blockId: string) {
    setLocalArticle(current => {
      const base = current ?? article
      if (!base) return current
      return { ...base, blocks: base.blocks.filter(b => b.id !== blockId) }
    })
  }

  function handleReorderBlocks(fromBlockId: string, toBlockId: string) {
    setLocalArticle(current => {
      const base = current ?? article
      if (!base) return current
      const oldIndex = base.blocks.findIndex(b => b.id === fromBlockId)
      const newIndex = base.blocks.findIndex(b => b.id === toBlockId)
      if (oldIndex === -1 || newIndex === -1) return current
      return { ...base, blocks: arrayMove(base.blocks, oldIndex, newIndex) }
    })
  }

  function handleInsertBlock(blockType: string, afterBlockId: string | null) {
    const newId = `new-${Date.now()}`
    setLocalArticle(current => {
      const base = current ?? article
      if (!base) return current
      const cls = base.classification

      let newBlock: WireArticleBlock
      switch (blockType) {
        case 'heading':
          newBlock = { id: newId, type: 'heading', classification: cls, content: 'New Heading', level: 2 }
          break
        case 'list':
          newBlock = { id: newId, type: 'list', classification: cls, style: 'unordered', items: [] }
          break
        case 'section-marker':
          newBlock = { id: newId, type: 'section-marker', classification: cls, sectionType: 'summary' }
          break
        case 'source-list':
          newBlock = { id: newId, type: 'source-list', classification: cls, entries: [] }
          break
        case 'photo':
          newBlock = { id: newId, type: 'photo', classification: cls, photo: { id: `${newId}-img`, artifactId: '', classification: cls, captionClassification: cls, expandable: false } }
          break
        case 'photo-group':
          newBlock = { id: newId, type: 'photo-group', classification: cls, photos: [], width: 'column', display: { type: 'grid', columns: 2 } }
          break
        case 'photo-comparison':
          newBlock = {
            id: newId, type: 'photo-comparison', classification: cls,
            before: { id: `${newId}-before`, artifactId: '', classification: cls, captionClassification: cls, expandable: false },
            after:  { id: `${newId}-after`,  artifactId: '', classification: cls, captionClassification: cls, expandable: false },
          }
          break
        case 'visual':
          newBlock = { id: newId, type: 'visual', classification: cls, artifactId: '' }
          break
        case 'video':
          newBlock = { id: newId, type: 'video', classification: cls, artifactId: '' }
          break
        case 'audio':
          newBlock = { id: newId, type: 'audio', classification: cls, artifactId: '', autoplay: false }
          break
        default:
          newBlock = { id: newId, type: 'paragraph', classification: cls, children: [{ type: 'span', text: '' }] }
      }

      const visibleTypes = new Set(['classification-banner', 'report-details', 'attribution'])
      let insertIdx: number
      if (afterBlockId === null) {
        // Insert after the last non-metadata block
        const lastVisibleIdx = [...base.blocks].reduce(
          (acc, b, i) => (!visibleTypes.has(b.type) ? i : acc),
          0,
        )
        insertIdx = lastVisibleIdx + 1
      } else {
        const afterIdx = base.blocks.findIndex(b => b.id === afterBlockId)
        insertIdx = afterIdx >= 0 ? afterIdx + 1 : base.blocks.length
      }

      return {
        ...base,
        blocks: [...base.blocks.slice(0, insertIdx), newBlock, ...base.blocks.slice(insertIdx)],
      }
    })
    setSelectedBlockId(newId)
    setBlockPaletteOpen(false)
  }

  function handleSwapComparisonSlots(blockId: string) {
    setLocalArticle(current => {
      const base = current ?? article
      if (!base) return current
      return {
        ...base,
        blocks: base.blocks.map(b => {
          if (b.id !== blockId || b.type !== 'photo-comparison') return b
          return { ...b, before: b.after, after: b.before }
        }),
      }
    })
  }

  function handleMovePhotoInGroup(blockId: string, fromIdx: number, toIdx: number) {
    setLocalArticle(current => {
      const base = current ?? article
      if (!base) return current
      return {
        ...base,
        blocks: base.blocks.map(b => {
          if (b.id !== blockId || b.type !== 'photo-group') return b
          if (toIdx < 0 || toIdx >= b.photos.length) return b
          return { ...b, photos: arrayMove(b.photos, fromIdx, toIdx) }
        }),
      }
    })
  }

  function handleAddArtifact(artifact: WireArtifact) {
    setLocalArtifacts(current => [...(current ?? article?.artifacts ?? []), artifact])
    // Invalidate so the query re-fetches with a proper presignedUrl for the new artifact.
    // The useEffect below will sync the fresh server URLs into localArtifacts once resolved.
    queryClient.invalidateQueries({ queryKey: articleKeys.reviewDetail(id) })
  }

  function handleMetaUpdate(patch: Partial<WireArticleFull>) {
    setLocalArticle(current => {
      const base = current ?? article
      if (!base) return current
      return { ...base, ...patch }
    })
  }

  const { artifactService } = useServices()

  async function handleRemoveArtifact(artifactId: string) {
    try {
      await artifactService.delete(id, artifactId)
      setLocalArtifacts(current => (current ?? article?.artifacts ?? []).filter(a => a.id !== artifactId))
    } catch {
      toast.error('Failed to delete artifact')
    }
  }

  // Derived article — used for both rendering and validation.
  // localArticle carries unsaved content edits; localArtifacts carries artifact
  // additions/removals which are already persisted and don't affect dirty state.
  const displayArticle: WireArticleFull | null = useMemo(() => {
    const base = localArticle ?? article ?? null
    if (!base) return null
    if (localArtifacts === null) return base
    return { ...base, artifacts: localArtifacts }
  }, [localArticle, article, localArtifacts])

  // Broadcast live draft to any open popout preview window.
  useArticlePreviewBroadcast(id ?? '', displayArticle)

  function openPopoutPreview() {
    window.open(`/admin/articles/${id}/live-preview`, 'wire-article-live-preview', 'width=1440,height=900')
  }

  // Re-run validation whenever the displayed article changes
  const validationResult = useMemo(
    () => displayArticle ? validateArticle(displayArticle) : null,
    [displayArticle]
  )

  // ── Loading ──────────────────────────────────────────────────────────────────

  if (isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 size={20} className="animate-spin text-fg-secondary" />
      </div>
    )
  }

  // ── Error ────────────────────────────────────────────────────────────────────

  if (isError || !article) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="flex items-center gap-2 rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          <AlertCircle size={14} aria-hidden="true" />
          {error instanceof Error ? error.message : 'Article not found'}
        </div>
      </div>
    )
  }

  // ── Three-panel shell ────────────────────────────────────────────────────────

  // displayArticle and validationResult are derived before early returns (above)
  // to satisfy Rules of Hooks. After this point article is guaranteed non-null.
  const safeArticle = displayArticle!

  // ── Read-only mode ────────────────────────────────────────────────────────────────────────────
  const _wf    = safeArticle.workflow
  const _stage = _wf.stage
  const access = getWorkflowActionAccess(safeArticle, currentUserSub)
  const toolbarActions = new Set(getArticleToolbarWorkflowActions(safeArticle, currentUserSub))
  const isQcHolder = access.isQcHolder
  const isLayoutHolder = access.isLayoutHolder
  const isEditorialHolder = access.isEditorialHolder
  const isPostPubHolder = access.isPostPubHolder
  const readOnly = !isQcHolder && !isLayoutHolder && !isEditorialHolder && !isPostPubHolder
  const canAddNotes = isWorkflowMember
  const canMoveToQC = access.canReturnToQc && !isLayoutHolder
  const canMoveToLayout = access.canPassToLayout && !isQcHolder

  const selectedBlock: WireArticleBlock | null =
    selectedBlockId
      ? (safeArticle.blocks.find(b => b.id === selectedBlockId) ?? null)
      : null

  function handleAutoResolve(error: ValidationError) {
    const resolved = applyResolution(safeArticle, error)
    if (resolved) setLocalArticle(resolved)
  }

  function handleAcknowledgeError(error: ValidationError) {
    // Persist the acknowledgement into localArticle so it survives re-renders
    const existing = safeArticle.validationErrors ?? []
    const alreadyStored = existing.some(e => e.code === error.code && 'blockId' in e === 'blockId' in error)
    const updated: WireArticleFull = {
      ...safeArticle,
      validationErrors: alreadyStored
        ? existing.map(e => e.code === error.code ? { ...e, acknowledged: true } : e)
        : [...existing, { ...error, acknowledged: true }],
    }
    setLocalArticle(updated)
  }

  return (
    <div className="relative flex overflow-hidden" style={{ height: 'calc(100vh - 3rem)' }}>

      {/* Unsaved-changes navigation guard dialog */}
      <Dialog.Root open={blocker.state === 'blocked'} onOpenChange={() => blocker.reset?.()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-6 shadow-xl outline-none"
            aria-describedby="nav-guard-desc"
          >
            <Dialog.Title className="mb-1 text-base font-semibold text-fg">
              Unsaved changes
            </Dialog.Title>
            <Dialog.Description id="nav-guard-desc" className="mb-6 text-sm text-fg-secondary">
              You have unsaved edits to this article. If you leave now, your changes will be lost.
            </Dialog.Description>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => blocker.reset?.()}
                className="rounded border border-line bg-canvas px-4 py-2 text-sm font-semibold text-fg transition-colors hover:bg-fill"
              >
                Stay and keep editing
              </button>
              <button
                type="button"
                onClick={() => blocker.proceed?.()}
                className="rounded border border-red-600 bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700"
              >
                Discard and leave
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Article preview — full-screen overlay */}
      <Dialog.Root open={previewOpen} onOpenChange={setPreviewOpen}>
        <Dialog.Portal>
          <Dialog.Content
            className="fixed inset-0 z-50 flex flex-col bg-canvas outline-none"
            aria-describedby={undefined}
          >
            <Dialog.Title className="sr-only">Article Preview</Dialog.Title>

            {/* Dismiss bar */}
            <div className="flex shrink-0 items-center justify-between border-b border-line bg-accent-tertiary-fill px-6 py-2.5">
              <div className="flex items-center gap-2">
                <Eye size={14} className="text-fg-secondary" aria-hidden="true" />
                <span className="text-xs font-bold uppercase tracking-widest text-fg-secondary">Article Preview</span>
                <span className="rounded bg-warning-fill px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-warning-fg">
                  Draft — not published
                </span>
              </div>
              <div className="flex items-center gap-2">
                <DarkModeToggle className="flex h-7 w-7 items-center justify-center rounded border border-line bg-canvas text-fg transition-colors hover:bg-fill" />
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
                    aria-label="Close preview"
                  >
                    <X size={12} aria-hidden="true" />
                    Close Preview
                  </button>
                </Dialog.Close>
              </div>
            </div>

            {/* Reader navbar */}
            <Navbar />

            {/* Scrollable article */}
            <div className="flex-1 overflow-y-auto">
              <ArticleRenderer article={safeArticle} />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Left icon rail + overlay ToC */}
      <ArticleTocShelf
        article={safeArticle}
        activeSectionId={activeSectionId}
        tocOpen={tocOpen}
        onTocToggle={() => { setBlockPaletteOpen(false); setTocOpen(o => !o) }}
        onSectionClick={handleSectionClick}
        blockPaletteOpen={blockPaletteOpen}
        onBlockPaletteToggle={() => { setTocOpen(false); setBlockPaletteOpen(o => !o) }}
        onInsertBlock={type => handleInsertBlock(type, null)}
      />

      {/* Center — toolbar + article canvas */}
      <section className="flex flex-1 flex-col overflow-hidden">

        {/* Toolbar */}
        <div className="flex shrink-0 items-center gap-3 border-b border-line bg-accent-tertiary-fill px-5 py-2">

          <div className="flex min-w-0 flex-1 items-center gap-3">
            <h1 className="truncate text-sm font-semibold text-fg" title={safeArticle.title}>
              {safeArticle.title}
            </h1>
            <StatusBadge stage={safeArticle.workflow.stage} />
            {safeArticle.documentId && (
              <span className="hidden shrink-0 font-mono text-[10px] text-fg-secondary xl:block">
                {safeArticle.documentId}
              </span>
            )}
          </div>

          {/* Metadata button */}
          <button
            type="button"
            onClick={() => setMetaDrawerOpen(true)}
            className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
            title="Edit article metadata"
          >
            <Settings2 size={12} aria-hidden="true" />
            Metadata
          </button>

          {/* Artifacts button */}
          <button
            type="button"
            onClick={() => setArtifactsDrawerOpen(true)}
            className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
            title="View attached artifacts"
          >
            <Paperclip size={12} aria-hidden="true" />
            Artifacts
            {(safeArticle.artifacts?.length ?? 0) > 0 && (
              <span className="ml-0.5 rounded-full bg-fill px-1.5 py-0.5 text-[10px] font-bold text-fg-secondary">
                {safeArticle.artifacts!.length}
              </span>
            )}
          </button>

          {/* Changelog button */}
          <button
            type="button"
            onClick={() => setChangelogDrawerOpen(true)}
            className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
            title="View revision history"
          >
            <History size={12} aria-hidden="true" />
            History
          </button>

          {/* Revisions & Recall button */}
          <button
            type="button"
            onClick={() => setRevisionsDrawerOpen(true)}
            className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
            title="Manage revision notices and recall"
          >
            <RotateCcw size={12} aria-hidden="true" />
            Revisions
            {(safeArticle.revisions?.length ?? 0) > 0 && (
              <span className="ml-0.5 rounded-full bg-fill px-1.5 py-0.5 text-[10px] font-bold text-fg-secondary">
                {safeArticle.revisions!.length}
              </span>
            )}
          </button>

          {/* Save button — shown when there are unsaved edits and the user can edit */}
          {localArticle !== null && !readOnly && (
            <button
              type="button"
              onClick={() => saveMutation.mutate(safeArticle, {
                onSuccess: () => { setLocalArticle(null); toast.success('Article saved.') },
                onError: () => toast.error('Save failed — please try again.'),
              })}
              disabled={saveMutation.isPending}
              className="flex items-center gap-1.5 rounded border border-blue-600 bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
              title="Save article changes"
            >
              {saveMutation.isPending
                ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                : <Save size={12} aria-hidden="true" />}
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          )}

          {/* Preview split button */}
          <div className="flex items-stretch overflow-hidden rounded border border-line bg-canvas text-xs font-semibold text-fg-secondary">
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 transition-colors hover:bg-fill hover:text-fg"
              title="Preview article as it will appear to readers"
            >
              <Eye size={12} aria-hidden="true" />
              Preview
            </button>
            <div className="w-px self-stretch bg-line" aria-hidden="true" />
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="flex items-center px-1.5 py-1.5 transition-colors hover:bg-fill hover:text-fg"
                  aria-label="More preview options"
                >
                  <ChevronDown size={11} aria-hidden="true" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  sideOffset={6}
                  align="end"
                  className="z-50 min-w-48 rounded border border-line bg-canvas py-1 shadow-lg"
                >
                  <DropdownMenu.Item
                    onSelect={openPopoutPreview}
                    className="flex cursor-default items-center gap-2 px-3 py-1.5 text-xs text-fg outline-none hover:bg-fill focus:bg-fill"
                  >
                    <ExternalLink size={12} aria-hidden="true" />
                    Open in new window
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>

          {/* Sign-off / editorial action — context-sensitive for the current stage holder */}
          {(() => {
            const qcSigned = access.qcSigned
            const layoutSigned = access.layoutSigned
            const editApproved = access.editorialApproved

            if (isQcHolder) return (
              <div className="flex items-center gap-2">
                {toolbarActions.has('qc_signoff') && !qcSigned && (
                  <button
                    type="button"
                    onClick={() => workflowAction.mutate(
                      { type: 'qc_signoff', id: safeArticle.id, by: currentUserSub, byName: user?.name },
                      {
                        onSuccess: () => toast.success(layoutSigned ? 'Signed off — sent to Editorial' : 'Signed off — passed to Layout'),
                        onError: () => toast.error('Failed to record sign-off'),
                      },
                    )}
                    disabled={workflowAction.isPending || (validationResult?.hasBlockingErrors ?? false)}
                    className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill disabled:cursor-not-allowed disabled:opacity-40"
                    title={validationResult?.hasBlockingErrors ? 'Resolve all errors before signing off' : undefined}
                  >
                    {workflowAction.isPending
                      ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                      : <PenLine size={12} aria-hidden="true" />}
                    Sign Off as QC
                  </button>
                )}
                {qcSigned && (
                  <span className="flex items-center gap-1.5 rounded px-2 py-1.5 text-xs font-semibold" style={{ color: 'var(--jade-11)' }}>
                    <Check size={12} aria-hidden="true" /> QC Signed Off
                  </span>
                )}
                {toolbarActions.has('pass_to_layout') && (
                  <button
                    type="button"
                    onClick={() => workflowAction.mutate(
                      { type: 'handoff', id: safeArticle.id, targetStage: 'layout_review' },
                      {
                        onSuccess: () => toast.success('Passed to Layout'),
                        onError: () => toast.error('Failed to update workflow'),
                      },
                    )}
                    disabled={workflowAction.isPending}
                    className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill disabled:opacity-40"
                  >
                    {workflowAction.isPending
                      ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                      : <ArrowRight size={12} aria-hidden="true" />}
                    Pass to Layout
                  </button>
                )}
              </div>
            )

            if (isLayoutHolder && toolbarActions.has('layout_signoff') && !layoutSigned) return (
              <button
                type="button"
                onClick={() => workflowAction.mutate(
                  { type: 'layout_signoff', id: safeArticle.id, by: currentUserSub, byName: user?.name },
                  {
                    onSuccess: () => toast.success(qcSigned ? 'Signed off — sent to Editorial' : 'Signed off — returned to QC'),
                    onError: () => toast.error('Failed to record sign-off'),
                  },
                )}
                disabled={workflowAction.isPending}
                className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill disabled:opacity-40"
              >
                {workflowAction.isPending
                  ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                  : <PenLine size={12} aria-hidden="true" />}
                Sign Off as Layout
              </button>
            )

            if (isLayoutHolder && layoutSigned) return (
              <span className="flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold" style={{ color: 'var(--jade-11)' }}>
                <Check size={12} aria-hidden="true" /> Layout Signed Off
              </span>
            )

            if (isEditorialHolder && toolbarActions.has('editorial_approve') && toolbarActions.has('editorial_reject') && !editApproved) return (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setScheduleDate(defaultScheduleDatetime())
                    setApproveDialogOpen(true)
                  }}
                  disabled={workflowAction.isPending}
                  className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill disabled:opacity-40"
                >
                  {workflowAction.isPending
                    ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                    : <CalendarCheck size={12} className="text-accent-primary-fg" aria-hidden="true" />}
                  Approve&hellip;
                </button>
                <button
                  type="button"
                  onClick={() => toast.info('Rejection flow — use the pipeline shelf for now')}
                  disabled={workflowAction.isPending}
                  className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-fill disabled:opacity-40"
                  style={{ color: 'var(--red-11)' }}
                >
                  <XCircle size={12} aria-hidden="true" />
                  Reject
                </button>
              </div>
            )

            if (isEditorialHolder && editApproved) return (
              <span className="flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold" style={{ color: 'var(--jade-11)' }}>
                <Check size={12} aria-hidden="true" /> Approved
              </span>
            )

            if (access.isPostPubHolder) return (
              <button
                type="button"
                onClick={() => setPostPubRevisionDialogOpen(true)}
                disabled={workflowAction.isPending || !canIssueRevision}
                title={!canIssueRevision ? 'You do not have permission to issue revisions' : undefined}
                className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-fill disabled:cursor-not-allowed disabled:opacity-40"
                style={{ color: 'var(--red-11)' }}
              >
                {workflowAction.isPending
                  ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                  : <AlertTriangle size={12} aria-hidden="true" />}
                Return to QC for Revision&hellip;
              </button>
            )

            return null
          })()}
        </div>

        {/* Article-level validation banner — sticky below toolbar */}
        <ArticleValidationBanner
          errors={validationResult?.errors ?? []}
          onAutoResolve={handleAutoResolve}
          onAcknowledge={handleAcknowledgeError}
        />

        {/* Read-only mode banner */}
        {readOnly && (() => {
          const stageLabel = STATUS_META[_stage]?.label ?? _stage
          const isEditableStage = ['qc_review', 'layout_review', 'editorial_approval'].includes(_stage as string)
          let message: string
          let actionLabel: string | null = null
          let actionStage: 'qc_review' | 'layout_review' | null = null
          const isAmber = canMoveToQC || canMoveToLayout
          if (canMoveToQC) {
            message = `This article is in ${stageLabel}. As the assigned QC reviewer, you can move it to QC Review.`
            actionLabel = 'Move to QC Review'
            actionStage = 'qc_review'
          } else if (canMoveToLayout) {
            message = `This article is in ${stageLabel}. As the assigned Layout reviewer, you can advance it to Layout Review.`
            actionLabel = 'Move to Layout Review'
            actionStage = 'layout_review'
          } else if (!isEditableStage) {
            message = `This article is ${stageLabel.toLowerCase()} — editing is not available.`
          } else {
            message = `This article is in ${stageLabel}. You don’t have edit access at this stage — viewing in read-only mode.`
          }
          return (
            <div
              className="flex shrink-0 items-center gap-3 border-b px-5 py-3"
              style={{
                backgroundColor: isAmber ? 'var(--amber-2)' : 'var(--gray-2)',
                borderColor: isAmber ? 'var(--amber-6)' : 'var(--gray-5)',
              }}
            >
              <Lock
                size={13}
                aria-hidden="true"
                className="shrink-0"
                style={{ color: isAmber ? 'var(--amber-9)' : 'var(--gray-9)' }}
              />
              <p
                className="flex-1 text-xs leading-snug"
                style={{ color: isAmber ? 'var(--amber-11)' : 'var(--gray-11)' }}
              >
                {message}
              </p>
              {actionLabel && actionStage && (
                <button
                  type="button"
                  onClick={() => workflowAction.mutate(
                    { type: 'handoff', id: safeArticle.id, targetStage: actionStage! },
                    {
                      onSuccess: () => toast.success(actionStage === 'qc_review' ? 'Moved to QC Review' : 'Moved to Layout Review'),
                      onError: () => toast.error('Failed to move article'),
                    },
                  )}
                  disabled={workflowAction.isPending}
                  className="flex shrink-0 items-center gap-1.5 rounded border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50"
                  style={{
                    borderColor: 'var(--amber-7)',
                    color: 'var(--amber-11)',
                    backgroundColor: 'var(--amber-3)',
                  }}
                >
                  {workflowAction.isPending
                    ? <Loader2 size={11} className="animate-spin" aria-hidden="true" />
                    : <ArrowRight size={11} aria-hidden="true" />}
                  {actionLabel}
                </button>
              )}
            </div>
          )
        })()}

        {/* Scrollable article canvas */}
        <ArticleCanvas
          article={safeArticle}
          selectedBlockId={selectedBlockId}
          onBlockSelect={setSelectedBlockId}
          blockRefs={blockRefs}
          editingBlockId={editingBlockId}
          onEnterEdit={setEditingBlockId}
          onSaveEdit={handleSaveEdit}
          onCancelEdit={() => setEditingBlockId(null)}
          onUpdateBlock={handleUpdateBlock}
          validationErrors={validationResult?.errors ?? []}
          onAutoResolve={handleAutoResolve}
          onAcknowledgeError={handleAcknowledgeError}
          onMoveBlock={handleMoveBlock}
          onDeleteBlock={handleDeleteBlock}
          onMovePhotoInGroup={handleMovePhotoInGroup}
          onSwapComparisonSlots={handleSwapComparisonSlots}
          onInsertBlock={handleInsertBlock}
          onReorderBlocks={handleReorderBlocks}
          notes={allNotes}
          onAddNote={(blockId, content, isPrivate) => createNote.mutate({ blockId, content, isPrivate })}
          onDeleteNote={(noteId) => deleteNote.mutate(noteId)}
          readOnly={readOnly}
          notesReadOnly={!canAddNotes}
        />

      </section>

      {/* Right — block properties panel */}
      {!readOnly && (
        <ArticleBlockInspector
          key={selectedBlock?.id ?? 'none'}
          selectedBlock={selectedBlock}
          onClearSelection={() => setSelectedBlockId(null)}
          onSaveBlockMeta={handleSaveBlockMeta}
          onUpdateBlock={handleUpdateBlock}
          imageArtifacts={safeArticle.artifacts?.filter((a): a is WireImageArtifact => a.type === 'image') ?? []}
          visualArtifacts={safeArticle.artifacts?.filter((a): a is WireVisualArtifact => a.type === 'visual') ?? []}
          videoArtifacts={safeArticle.artifacts?.filter((a): a is WireVideoArtifact => a.type === 'video') ?? []}
          captionsArtifacts={safeArticle.artifacts?.filter((a): a is WireCaptionsArtifact => a.type === 'captions') ?? []}
          audioArtifacts={safeArticle.artifacts?.filter((a): a is WireAudioArtifact => a.type === 'audio') ?? []}
        />
      )}

      {/* Article metadata drawer */}
      <ArticleMetadataDrawer
        article={safeArticle}
        open={metaDrawerOpen}
        onClose={() => setMetaDrawerOpen(false)}
        onUpdate={handleMetaUpdate}
        imageArtifacts={safeArticle.artifacts?.filter((a): a is WireImageArtifact => a.type === 'image') ?? []}
        readOnly={readOnly}
      />

      {/* Article artifacts drawer */}
      <ArticleArtifactsDrawer
        article={safeArticle}
        open={artifactsDrawerOpen}
        onClose={() => setArtifactsDrawerOpen(false)}
        onAddArtifact={handleAddArtifact}
        onRemoveArtifact={handleRemoveArtifact}
        onSetThumbnail={(thumbnail) => handleMetaUpdate({ thumbnail })}
        readOnly={readOnly}
      />

      {/* Changelog drawer */}
      <ArticleChangelogDrawer
        draftId={safeArticle.id}
        open={changelogDrawerOpen}
        onClose={() => setChangelogDrawerOpen(false)}
      />

      {/* Revisions & Recall drawer */}
      <ArticleRevisionsDrawer
        article={safeArticle}
        open={revisionsDrawerOpen}
        onClose={() => setRevisionsDrawerOpen(false)}
        onUpdate={handleMetaUpdate}
      />

      {/* Editorial approval / schedule dialog */}
      <Dialog.Root
        open={approveDialogOpen}
        onOpenChange={(v) => { setApproveDialogOpen(v); if (!v) setScheduleDate('') }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-96 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-5 shadow-xl focus:outline-none"
            aria-describedby="approve-dialog-desc"
          >
            <Dialog.Title className="text-sm font-semibold text-fg">
              Approve for publication
            </Dialog.Title>
            <Dialog.Description id="approve-dialog-desc" className="mt-1 text-xs text-fg-secondary">
              Schedule a future publication time, or publish the article immediately.
            </Dialog.Description>
            <div className="mt-4">
              <label className="block text-[11px] font-semibold text-fg-secondary" htmlFor="ar-schedule-datetime">
                Publication date &amp; time
              </label>
              <div className="mt-1.5 flex gap-1.5">
                <input
                  id="ar-schedule-datetime"
                  type="datetime-local"
                  className="flex-1 rounded border border-line bg-surface px-3 py-2 text-[12px] text-fg focus:border-line-strong focus:outline-none"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                />
                <button
                  type="button"
                  onClick={(e) => (e.currentTarget.previousElementSibling as HTMLInputElement)?.blur()}
                  className="rounded border border-line bg-canvas px-3 py-2 text-xs font-semibold text-accent-primary-fg transition-colors hover:bg-fill"
                >
                  OK
                </button>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => handleEditorialApprove(true)}
                disabled={workflowAction.isPending}
                className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill disabled:opacity-40"
              >
                {workflowAction.isPending
                  ? <Loader2 size={11} className="animate-spin shrink-0" aria-hidden="true" />
                  : <Check size={11} className="shrink-0" aria-hidden="true" />}
                Publish now
              </button>
              <div className="flex gap-2">
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
                  >
                    Cancel
                  </button>
                </Dialog.Close>
                <button
                  type="button"
                  onClick={() => handleEditorialApprove(false)}
                  disabled={!scheduleDate || workflowAction.isPending}
                  className="rounded bg-jade-9 px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-40"
                >
                  {workflowAction.isPending ? 'Scheduling…' : 'Schedule'}
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Post-pub revision dialog */}
      <Dialog.Root
        open={postPubRevisionDialogOpen}
        onOpenChange={(v) => { setPostPubRevisionDialogOpen(v); if (!v) setPostPubRevisionNote('') }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-96 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-5 shadow-xl focus:outline-none"
            aria-describedby="post-pub-revision-desc"
          >
            <Dialog.Title className="text-sm font-semibold text-fg">
              Return to QC for Revision
            </Dialog.Title>
            <Dialog.Description id="post-pub-revision-desc" className="mt-1 text-xs text-fg-secondary">
              The article will remain live while QC addresses the issue.
            </Dialog.Description>
            <textarea
              className="mt-3 w-full rounded border border-line bg-surface px-3 py-2 text-[12px] text-fg placeholder:text-fg-secondary focus:border-line-strong focus:outline-none"
              rows={4}
              placeholder="Describe what needs to be corrected…"
              value={postPubRevisionNote}
              onChange={(e) => setPostPubRevisionNote(e.target.value)}
              autoFocus
            />
            <div className="mt-4 flex justify-end gap-2">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                onClick={handlePostPubRevision}
                disabled={!postPubRevisionNote.trim() || workflowAction.isPending}
                className="rounded bg-red-9 px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-40"
              >
                {workflowAction.isPending ? 'Sending…' : 'Send to QC'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

    </div>
  )
}

export default ArticleReviewer
